import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useUser, useUserPreferences } from './UserContext';
import { ArtifactService } from '../services/api/artifactService';
import { companyModelSelectionFromTree } from '../utils/companyModelSelectionFromTree';

export type OperationalMode = 'architect' | 'operator' | 'coach' | 'integrator' | 'development' | 'marketfit';
export type Environment = 'Prime' | 'Company' | 'Sandbox';
export type DataSource = 'sample' | 'live';
export type ArtifactLevel = 'artifact' | 'artifactItem' | 'process';

export interface ModelSelectorState {
  // UI Role - determines what interface elements are shown
  uiRole: OperationalMode;
  
  // Environment - determines which data model to use
  environment: Environment;
  
  // Data Source - determines if using sample or live data
  dataSource: DataSource;
  
  // Artifact Level - determines the granularity of artifact display
  artifactLevel: ArtifactLevel;
  
  // Model Selection - specific model instances
  selectedPrimeModel: PrimeModelSelection | null;
  selectedCompanyModel: CompanyModelSelection | null;
  
  // Context switching
  isSwitching: boolean;
  lastSwitchTime: string | null;
  
  // Validation state
  isValidConfiguration: boolean;
  validationErrors: string[];
}

export interface PrimeModelSelection {
  id: string;
  name: string;
  description?: string;
  rootArtifactId: string;
  rootArtifactName: string;
  artifactCount: number;
  lastUpdated: string;
}

export interface CompanyModelSelection {
  id: string;
  name: string;
  description?: string;
  businessModelId: string;
  businessModelName: string;
  artifactTreeId: string;
  artifactTreeName: string;
  artifactCount: number;
  lastUpdated: string;
}

interface ModelSelectorContextType extends ModelSelectorState {
  // Role Management
  setUIRole: (role: OperationalMode) => void;
  switchToRole: (role: OperationalMode) => Promise<void>;
  
  // Environment Management
  setEnvironment: (environment: Environment) => void;
  switchToEnvironment: (environment: Environment) => Promise<void>;
  
  // Data Source Management
  setDataSource: (source: DataSource) => void;
  switchToDataSource: (source: DataSource) => Promise<void>;
  
  // Artifact Level Management
  setArtifactLevel: (level: ArtifactLevel) => void;
  
  // Model Selection Management
  setSelectedPrimeModel: (model: PrimeModelSelection | null) => void;
  setSelectedCompanyModel: (model: CompanyModelSelection | null) => void;
  getAvailablePrimeModels: () => Promise<PrimeModelSelection[]>;
  getAvailableCompanyModels: () => Promise<CompanyModelSelection[]>;
  getCurrentModelSelection: () => PrimeModelSelection | CompanyModelSelection | null;
  
  // Configuration Management
  resetToDefaults: () => void;
  validateConfiguration: () => boolean;
  getConfigurationSummary: () => string;
  
  // Context Persistence
  saveConfiguration: () => Promise<void>;
  loadConfiguration: () => Promise<void>;
  
  // Role-specific Helpers
  isArchitectMode: () => boolean;
  isOperatorMode: () => boolean;
  isCoachMode: () => boolean;
  canEditArtifacts: () => boolean;
  canExecuteProcesses: () => boolean;
  canViewAnalytics: () => boolean;
}

const ModelSelectorContext = createContext<ModelSelectorContextType | undefined>(undefined);

// Default configuration
// UX Principle #2: Execution Mode (operator) is the default, non-negotiable
// See: docs/EXO_PANEL_UX_PRINCIPLES.md
const DEFAULT_CONFIG: ModelSelectorState = {
  uiRole: 'operator', // Execution Mode is default per Exo Panel UX Principles
  environment: 'Company',
  dataSource: 'live',
  artifactLevel: 'artifact',
  selectedPrimeModel: null,
  selectedCompanyModel: null,
  isSwitching: false,
  lastSwitchTime: null,
  isValidConfiguration: true,
  validationErrors: [],
};

// Role capabilities mapping
const ROLE_CAPABILITIES = {
  architect: {
    canEditArtifacts: true,
    canExecuteProcesses: false,
    canViewAnalytics: true,
    canManageUsers: false,
    canConfigureSystem: true,
  },
  operator: {
    canEditArtifacts: false,
    canExecuteProcesses: true,
    canViewAnalytics: false,
    canManageUsers: false,
    canConfigureSystem: false,
  },
  coach: {
    canEditArtifacts: false,
    canExecuteProcesses: false,
    canViewAnalytics: true,
    canManageUsers: false,
    canConfigureSystem: false,
  },
  integrator: {
    canEditArtifacts: true,
    canExecuteProcesses: true,
    canViewAnalytics: true,
    canManageUsers: false,
    canConfigureSystem: true,
  },
  development: {
    canEditArtifacts: true,
    canExecuteProcesses: false,
    canViewAnalytics: true,
    canManageUsers: false,
    canConfigureSystem: true,
  },
  marketfit: {
    canEditArtifacts: false,
    canExecuteProcesses: false,
    canViewAnalytics: true,
    canManageUsers: false,
    canConfigureSystem: false,
  },
};

export const ModelSelectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { preferences, updatePreferences } = useUserPreferences();
  const { selectedCompany, availableCompanies } = useUser();
  const [state, setState] = useState<ModelSelectorState>(() => ({
    ...DEFAULT_CONFIG,
    uiRole: preferences.defaultUIRole,
    environment: preferences.defaultEnvironment,
  }));
  const companyModelSyncGenRef = useRef(0);
  const accessibleCompanyIdsKey = useMemo(
    () => availableCompanies.map((uc) => uc.company.id).sort().join(','),
    [availableCompanies]
  );

  // Load configuration from user preferences on mount
  useEffect(() => {
    void loadConfiguration();
  }, []);

  // Save configuration to user preferences when it changes
  useEffect(() => {
    if (state.uiRole !== DEFAULT_CONFIG.uiRole || state.environment !== DEFAULT_CONFIG.environment) {
      void saveConfiguration();
    }
  }, [state.uiRole, state.environment]);

  // Keep selectedCompanyModel aligned with header Select Company
  useEffect(() => {
    const companyId = selectedCompany?.id;
    if (!companyId) {
      setState((prev) =>
        prev.selectedCompanyModel
          ? { ...prev, selectedCompanyModel: null, lastSwitchTime: new Date().toISOString() }
          : prev
      );
      return;
    }

    const generation = ++companyModelSyncGenRef.current;
    const accessibleCompanyIds = accessibleCompanyIdsKey
      ? accessibleCompanyIdsKey.split(',')
      : [];
    void (async () => {
      try {
        const trees = await ArtifactService.getTrees(companyId, accessibleCompanyIds);
        if (generation !== companyModelSyncGenRef.current) return;
        if (!trees.length) {
          setState((prev) => ({
            ...prev,
            selectedCompanyModel: null,
            lastSwitchTime: new Date().toISOString(),
          }));
          return;
        }
        const mapped = companyModelSelectionFromTree(trees[0], selectedCompany?.name);
        if (!mapped) return;
        setState((prev) => ({
          ...prev,
          // Do not yank architects out of Prime; still refresh the company model for helpers.
          environment: prev.environment === 'Prime' ? 'Prime' : 'Company',
          selectedCompanyModel: mapped,
          lastSwitchTime: new Date().toISOString(),
        }));
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[ModelSelector] Failed to sync company model after company switch:', err);
        }
      }
    })();
  }, [selectedCompany?.id, selectedCompany?.name, accessibleCompanyIdsKey]);

  const setUIRole = useCallback((role: OperationalMode) => {
    setState(prev => ({
      ...prev,
      uiRole: role,
      lastSwitchTime: new Date().toISOString(),
    }));
  }, []);

  const switchToRole = useCallback(async (role: OperationalMode) => {
    setState(prev => ({ ...prev, isSwitching: true }));
    
    try {
      // Simulate role switch delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setState(prev => ({
        ...prev,
        uiRole: role,
        isSwitching: false,
        lastSwitchTime: new Date().toISOString(),
      }));
    } catch (error) {
      setState(prev => ({ ...prev, isSwitching: false }));
      throw error;
    }
  }, []);

  const setEnvironment = useCallback((environment: Environment) => {
    setState(prev => ({
      ...prev,
      environment,
      lastSwitchTime: new Date().toISOString(),
    }));
  }, []);

  const switchToEnvironment = useCallback(async (environment: Environment) => {
    setState(prev => ({ ...prev, isSwitching: true }));
    
    try {
      // Simulate environment switch delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setState(prev => ({
        ...prev,
        environment,
        isSwitching: false,
        lastSwitchTime: new Date().toISOString(),
      }));
    } catch (error) {
      setState(prev => ({ ...prev, isSwitching: false }));
      throw error;
    }
  }, []);

  const setDataSource = useCallback((source: DataSource) => {
    setState(prev => ({
      ...prev,
      dataSource: source,
      lastSwitchTime: new Date().toISOString(),
    }));
  }, []);

  const switchToDataSource = useCallback(async (source: DataSource) => {
    setState(prev => ({ ...prev, isSwitching: true }));
    
    try {
      // Simulate data source switch delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setState(prev => ({
        ...prev,
        dataSource: source,
        isSwitching: false,
        lastSwitchTime: new Date().toISOString(),
      }));
    } catch (error) {
      setState(prev => ({ ...prev, isSwitching: false }));
      throw error;
    }
  }, []);

  const setArtifactLevel = useCallback((level: ArtifactLevel) => {
    setState(prev => ({
      ...prev,
      artifactLevel: level,
    }));
  }, []);

  const setSelectedPrimeModel = useCallback((model: PrimeModelSelection | null) => {
    setState(prev => ({
      ...prev,
      selectedPrimeModel: model,
      lastSwitchTime: new Date().toISOString(),
    }));
  }, []);

  const setSelectedCompanyModel = useCallback((model: CompanyModelSelection | null) => {
    setState(prev => ({
      ...prev,
      selectedCompanyModel: model,
      lastSwitchTime: new Date().toISOString(),
    }));
  }, []);

  const getAvailablePrimeModels = useCallback(async (): Promise<PrimeModelSelection[]> => {
    try {
      // In a real implementation, this would fetch from the API
      // For now, return mock data based on the prime model hierarchy
      const mockPrimeModels: PrimeModelSelection[] = [
        {
          id: 'prime-model-1',
          name: 'Krysalis Business Entity Model',
          description: 'Complete business entity model with all artifacts',
          rootArtifactId: 'pa-biz-entity',
          rootArtifactName: 'Krysalis Business Entity',
          artifactCount: 150,
          lastUpdated: new Date().toISOString(),
        },
        {
          id: 'prime-model-2',
          name: 'Product Development Model',
          description: 'Focused on product and market artifacts',
          rootArtifactId: 'pa-product',
          rootArtifactName: 'Product',
          artifactCount: 75,
          lastUpdated: new Date().toISOString(),
        },
      ];
      
      return mockPrimeModels;
    } catch (error) {
      console.error('Failed to fetch prime models:', error);
      return [];
    }
  }, []);

  const getAvailableCompanyModels = useCallback(async (): Promise<CompanyModelSelection[]> => {
    try {
      const companyId = selectedCompany?.id;
      if (!companyId) return [];
      const accessibleCompanyIds = accessibleCompanyIdsKey
        ? accessibleCompanyIdsKey.split(',')
        : [];
      const trees = await ArtifactService.getTrees(companyId, accessibleCompanyIds);
      return trees
        .map((tree) => companyModelSelectionFromTree(tree, selectedCompany?.name))
        .filter((m): m is CompanyModelSelection => m != null);
    } catch (error) {
      console.error('Failed to fetch company models:', error);
      return [];
    }
  }, [selectedCompany?.id, selectedCompany?.name, accessibleCompanyIdsKey]);

  const getCurrentModelSelection = useCallback(() => {
    if (state.environment === 'Prime') {
      return state.selectedPrimeModel;
    } else if (state.environment === 'Company') {
      return state.selectedCompanyModel;
    }
    return null;
  }, [state.environment, state.selectedPrimeModel, state.selectedCompanyModel]);

  const resetToDefaults = useCallback(() => {
    setState({
      ...DEFAULT_CONFIG,
      uiRole: preferences.defaultUIRole,
      environment: preferences.defaultEnvironment,
    });
  }, [preferences]);

  const validateConfiguration = useCallback((): boolean => {
    const errors: string[] = [];
    
    // Validate role and environment compatibility
    if (state.environment === 'Prime' && !['architect', 'development'].includes(state.uiRole)) {
      errors.push('Prime environment is only available for architect and development roles');
    }
    
    if (state.environment === 'Sandbox' && state.dataSource === 'live') {
      errors.push('Sandbox environment cannot use live data source');
    }
    
    // Validate artifact level compatibility
    if (state.artifactLevel === 'process' && state.environment === 'Prime') {
      errors.push('Process level is not available in Prime environment');
    }
    
    setState(prev => ({
      ...prev,
      isValidConfiguration: errors.length === 0,
      validationErrors: errors,
    }));
    
    return errors.length === 0;
  }, [state.environment, state.uiRole, state.dataSource, state.artifactLevel]);

  const getConfigurationSummary = useCallback((): string => {
    return `${state.uiRole} mode • ${state.environment} environment • ${state.dataSource} data • ${state.artifactLevel} level`;
  }, [state]);

  const saveConfiguration = useCallback(async () => {
    try {
      await updatePreferences({
        defaultUIRole: state.uiRole,
        defaultEnvironment: state.environment,
      });
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  }, [state.uiRole, state.environment, updatePreferences]);

  const loadConfiguration = useCallback(async () => {
    setState(prev => ({
      ...prev,
      uiRole: preferences.defaultUIRole,
      environment: preferences.defaultEnvironment,
    }));
  }, [preferences]);

  // Role-specific helper functions
  const isArchitectMode = useCallback(() => state.uiRole === 'architect', [state.uiRole]);
  const isOperatorMode = useCallback(() => state.uiRole === 'operator', [state.uiRole]);
  const isCoachMode = useCallback(() => state.uiRole === 'coach', [state.uiRole]);
  
  const canEditArtifacts = useCallback(() => {
    return ROLE_CAPABILITIES[state.uiRole]?.canEditArtifacts || false;
  }, [state.uiRole]);
  
  const canExecuteProcesses = useCallback(() => {
    return ROLE_CAPABILITIES[state.uiRole]?.canExecuteProcesses || false;
  }, [state.uiRole]);
  
  const canViewAnalytics = useCallback(() => {
    return ROLE_CAPABILITIES[state.uiRole]?.canViewAnalytics || false;
  }, [state.uiRole]);

  // Validate configuration whenever state changes
  useEffect(() => {
    validateConfiguration();
  }, [validateConfiguration]);

  const value: ModelSelectorContextType = {
    ...state,
    setUIRole,
    switchToRole,
    setEnvironment,
    switchToEnvironment,
    setDataSource,
    switchToDataSource,
    setArtifactLevel,
    setSelectedPrimeModel,
    setSelectedCompanyModel,
    getAvailablePrimeModels,
    getAvailableCompanyModels,
    getCurrentModelSelection,
    resetToDefaults,
    validateConfiguration,
    getConfigurationSummary,
    saveConfiguration,
    loadConfiguration,
    isArchitectMode,
    isOperatorMode,
    isCoachMode,
    canEditArtifacts,
    canExecuteProcesses,
    canViewAnalytics,
  };

  return (
    <ModelSelectorContext.Provider value={value}>
      {children}
    </ModelSelectorContext.Provider>
  );
};

export const useModelSelector = (): ModelSelectorContextType => {
  const context = useContext(ModelSelectorContext);
  if (context === undefined) {
    throw new Error('useModelSelector must be used within a ModelSelectorProvider');
  }
  return context;
};

// Convenience hooks for specific aspects
export const useUIRole = () => {
  const { uiRole, setUIRole, switchToRole, isArchitectMode, isOperatorMode, isCoachMode } = useModelSelector();
  return {
    uiRole,
    setUIRole,
    switchToRole,
    isArchitectMode,
    isOperatorMode,
    isCoachMode,
  };
};

export const useEnvironment = () => {
  const { environment, setEnvironment, switchToEnvironment } = useModelSelector();
  return {
    environment,
    setEnvironment,
    switchToEnvironment,
  };
};

export const useDataSource = () => {
  const { dataSource, setDataSource, switchToDataSource } = useModelSelector();
  return {
    dataSource,
    setDataSource,
    switchToDataSource,
  };
};
